// src/pages/UploadData.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
  X,
  ChevronDown,
  ChevronUp,
  BookOpen,
  XCircle,
  Tag,
} from 'lucide-react';
import Navbar from '../navbar';
import { getUserRoleFromToken } from '../../hydraApiService';

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

const getHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

function UploadData() {
  const navigate = useNavigate();

  const userRole       = getUserRoleFromToken();
  const isManufacturer = userRole === 'Manufacturer';
  const isRepairRefurb = userRole === 'Repair/Refurb';

  // ── Import Product File state (Manufacturer only) ──
  const [importExpanded, setImportExpanded] = useState(false);
  const [csvFile, setCsvFile]               = useState(null);
  const [csvLoading, setCsvLoading]         = useState(false);
  const [csvResult, setCsvResult]           = useState(null);
  const [csvError, setCsvError]             = useState(null);
  const csvFileRef = useRef(null);

  // ── Disassembly Instruction state ──
  const [disassemblyExpanded, setDisassemblyExpanded]   = useState(false);
  const [productGroups, setProductGroups]               = useState([]);
  const [groupsLoading, setGroupsLoading]               = useState(true);
  const [selectedGroup, setSelectedGroup]               = useState('');
  const [existingInstructions, setExistingInstructions] = useState(null);
  const [instructionsLoading, setInstructionsLoading]   = useState(false);
  const [selectedFile, setSelectedFile]                 = useState(null);
  const [disassemblyLoading, setDisassemblyLoading]     = useState(false);
  const [uploadResult, setUploadResult]                 = useState(null);
  const [disassemblyError, setDisassemblyError]         = useState(null);
  const fileInputRef = useRef(null);

  // ── Category Instruction state (Manufacturer only) ──
  const [categoryExpanded, setCategoryExpanded]           = useState(false);
  const [categories, setCategories]                       = useState([]);
  const [categoriesLoading, setCategoriesLoading]         = useState(false);
  const [catSelectedGroup, setCatSelectedGroup]           = useState('');
  const [catSelectedCategory, setCatSelectedCategory]     = useState('');
  const [catSelectedFile, setCatSelectedFile]             = useState(null);
  const [catLoading, setCatLoading]                       = useState(false);
  const [catUploadResult, setCatUploadResult]             = useState(null);
  const [catError, setCatError]                           = useState(null);
  const [catExistingData, setCatExistingData]             = useState(null);
  const [catExistingLoading, setCatExistingLoading]       = useState(false);
  const catFileInputRef = useRef(null);

  // ── Fetch product groups on mount ──
  useEffect(() => {
    const fetchProductGroups = async () => {
      try {
        setGroupsLoading(true);
        const response = await axios.get(`${API_BASE_URL}/product-groups`, {
          headers: getHeaders(),
        });
        setProductGroups(response.data?.data?.groups || []);
      } catch (err) {
        console.error('Failed to fetch product groups:', err);
        setDisassemblyError('Failed to load product groups. Please refresh the page.');
      } finally {
        setGroupsLoading(false);
      }
    };
    fetchProductGroups();
  }, []);

  // ── Fetch categories when Category card expands ──
  useEffect(() => {
    if (!categoryExpanded || categories.length > 0) return;
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await axios.get(`${API_BASE_URL}/dpp-categories`, {
          headers: getHeaders(),
        });
        setCategories(response.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        setCatError('Failed to load categories. Please refresh the page.');
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [categoryExpanded]);

  // ── Fetch existing category instruction when group + category both selected ──
  useEffect(() => {
    if (!catSelectedGroup || !catSelectedCategory) {
      setCatExistingData(null);
      return;
    }
    const fetchCatData = async () => {
      try {
        setCatExistingLoading(true);
        setCatExistingData(null);
        const response = await axios.get(
          `${API_BASE_URL}/upload-instruction/${encodeURIComponent(catSelectedGroup)}/${encodeURIComponent(catSelectedCategory)}`,
          { headers: getHeaders() }
        );
        if (response.data?.success && response.data?.data) {
          setCatExistingData(response.data.data);
        }
      } catch (err) {
        if (err.response?.status !== 404) console.error('Failed to fetch instruction data:', err);
      } finally {
        setCatExistingLoading(false);
      }
    };
    fetchCatData();
  }, [catSelectedGroup, catSelectedCategory]);

  // ── Fetch existing instructions when group changes (Disassembly) ──
  useEffect(() => {
    if (!selectedGroup) { setExistingInstructions(null); return; }
    const fetchInstructions = async () => {
      try {
        setInstructionsLoading(true);
        setExistingInstructions(null);
        const response = await axios.get(
          `${API_BASE_URL}/disassembly-instructions/${encodeURIComponent(selectedGroup)}`,
          { headers: getHeaders() }
        );
        if (response.data?.success && response.data?.data) {
          setExistingInstructions(response.data.data);
        }
      } catch (err) {
        if (err.response?.status !== 404) console.error('Failed to fetch instructions:', err);
      } finally {
        setInstructionsLoading(false);
      }
    };
    fetchInstructions();
  }, [selectedGroup]);

  // ── Import Product File handlers ──
  const handleToggleImport = () => {
    setImportExpanded(prev => !prev);
    setCsvFile(null);
    setCsvResult(null);
    setCsvError(null);
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv') || ['text/csv', 'application/vnd.ms-excel'].includes(file.type);
    if (!isCSV) { setCsvError('Please select a valid CSV file'); setCsvFile(null); return; }
    if (file.size > 10 * 1024 * 1024) { setCsvError('File size exceeds 10MB limit'); setCsvFile(null); return; }
    setCsvFile(file);
    setCsvError(null);
    setCsvResult(null);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) { setCsvError('Please select a CSV file'); return; }
    setCsvLoading(true);
    setCsvError(null);
    setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const response = await axios.post(`${API_BASE_URL}/dpp/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      if (response.data.success) {
        setCsvResult(response.data);
        setCsvFile(null);
        if (csvFileRef.current) csvFileRef.current.value = '';
        setTimeout(() => navigate('/master-data'), 2000);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('CSV upload error:', err);
      setCsvError(err.response?.data?.message || err.response?.data?.error || err.message || 'Upload failed. Please try again.');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleCsvCancel = () => {
    setCsvFile(null);
    setCsvError(null);
    setCsvResult(null);
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  // ── Disassembly Instruction handlers ──
  const handleToggleDisassembly = () => {
    setDisassemblyExpanded(prev => !prev);
    setSelectedGroup('');
    setSelectedFile(null);
    setUploadResult(null);
    setDisassemblyError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      setDisassemblyError('Please select a valid PDF file'); setSelectedFile(null); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setDisassemblyError('File size exceeds 20MB limit'); setSelectedFile(null); return;
    }
    setSelectedFile(file);
    setDisassemblyError(null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setDisassemblyError('Please select a PDF file'); return; }
    if (!selectedGroup) { setDisassemblyError('Please select a product group'); return; }
    setDisassemblyLoading(true);
    setDisassemblyError(null);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('productGroup', selectedGroup);
      const response = await axios.post(
        `${API_BASE_URL}/disassembly-instructions`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      if (response.data.success) {
        setUploadResult(response.data);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (response.data.data) setExistingInstructions(response.data.data);
        // ── Redirect to dashboard after disassembly upload ──
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setDisassemblyError(err.response?.data?.message || err.response?.data?.error || err.message || 'Upload failed. Please try again.');
    } finally {
      setDisassemblyLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setDisassemblyError(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Category Instruction handlers ──
  const handleToggleCategory = () => {
    setCategoryExpanded(prev => !prev);
    setCatSelectedGroup('');
    setCatSelectedCategory('');
    setCatSelectedFile(null);
    setCatUploadResult(null);
    setCatError(null);
    setCatExistingData(null);
    if (catFileInputRef.current) catFileInputRef.current.value = '';
  };

  const handleCatFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      setCatError('Please select a valid PDF file'); setCatSelectedFile(null); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setCatError('File size exceeds 20MB limit'); setCatSelectedFile(null); return;
    }
    setCatSelectedFile(file);
    setCatError(null);
    setCatUploadResult(null);
  };

  const handleCatUpload = async () => {
    if (!catSelectedFile)     { setCatError('Please select a PDF file'); return; }
    if (!catSelectedGroup)    { setCatError('Please select a product group'); return; }
    if (!catSelectedCategory) { setCatError('Please select a category'); return; }
    setCatLoading(true);
    setCatError(null);
    setCatUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', catSelectedFile);
      formData.append('productGroup', catSelectedGroup);
      formData.append('categoryKey', catSelectedCategory);
      const response = await axios.post(
        `${API_BASE_URL}/upload-instruction`,
        formData,
        { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      if (response.data.success) {
        setCatUploadResult(response.data);
        setCatSelectedFile(null);
        if (catFileInputRef.current) catFileInputRef.current.value = '';
        if (response.data.data) setCatExistingData(response.data.data);
        // ── Redirect to product group page after category instruction upload ──
        setTimeout(() => navigate('/master-data'), 2000);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Category upload error:', err);
      setCatError(err.response?.data?.message || err.response?.data?.error || err.message || 'Upload failed. Please try again.');
    } finally {
      setCatLoading(false);
    }
  };

  const handleCatCancel = () => {
    setCatSelectedFile(null);
    setCatError(null);
    setCatUploadResult(null);
    if (catFileInputRef.current) catFileInputRef.current.value = '';
  };

  // ── Location Master CSV state (Manufacturer only) ──
  const [locationExpanded, setLocationExpanded]       = useState(false);
  const [locationFiles, setLocationFiles]             = useState([]);
  const [locationLoading, setLocationLoading]         = useState(false);
  const [locationError, setLocationError]             = useState(null);
  const [locationUploadError, setLocationUploadError] = useState(null);
  const [locationResult, setLocationResult]           = useState(null);
  const locationFileRef = useRef(null);

  const LOCATION_CSV_URL = 'https://dpp-api.acespireconsulting.com/api/locations/upload-csv';

  const handleToggleLocation = () => {
    setLocationExpanded(prev => !prev);
    setLocationFiles([]);
    setLocationError(null);
    setLocationUploadError(null);
    setLocationResult(null);
    if (locationFileRef.current) locationFileRef.current.value = '';
  };

  const handleLocationFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const errors = [];
    const validated = files.filter((file) => {
      const isCSV = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
      const underLimit = file.size <= 10 * 1024 * 1024;
      if (!isCSV) errors.push(`"${file.name}" is not a CSV`);
      else if (!underLimit) errors.push(`"${file.name}" exceeds 10MB`);
      return isCSV && underLimit;
    });
    setLocationError(errors.length ? errors.join(' · ') : null);
    setLocationFiles(validated);
    setLocationUploadError(null);
    setLocationResult(null);
  };

  const handleLocationUpload = async () => {
    if (!locationFiles.length) { setLocationError('Please select a file first.'); return; }
    setLocationLoading(true);
    setLocationError(null);
    setLocationUploadError(null);
    setLocationResult(null);
    try {
      const formData = new FormData();
      locationFiles.forEach((f) => formData.append('files', f));
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
      const response = await axios.post(LOCATION_CSV_URL, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const { accepted = [], rejected = [] } = response.data?.data || {};
      if (response.data.success && !rejected.length) {
        setLocationResult({ accepted });
        setLocationFiles([]);
        if (locationFileRef.current) locationFileRef.current.value = '';
        // ── Redirect to dashboard after location master upload ──
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setLocationUploadError({ accepted, rejected });
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.data?.accepted || data?.data?.rejected) {
        setLocationUploadError({ accepted: data.data.accepted || [], rejected: data.data.rejected || [] });
      } else {
        setLocationError(data?.message || data?.error || err.message || 'Upload failed.');
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const handleLocationCancel = () => {
    setLocationFiles([]);
    setLocationError(null);
    setLocationUploadError(null);
    setLocationResult(null);
    if (locationFileRef.current) locationFileRef.current.value = '';
  };

  const removeLocationFile = (index) => {
    setLocationFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Header */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl shadow">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />Back
        </button>
        <h2 className="text-blue-600 font-medium text-sm sm:text-base">Upload Data</h2>
      </div>

      {/* Cards */}
      <div className="p-4 sm:p-6 lg:p-8 max-w-xl mx-auto flex flex-col gap-4">

        {/* ══════════════════════════════════════
            CARD 1 — Import Product File
            Visible to: Manufacturer only
        ══════════════════════════════════════ */}
        {isManufacturer && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">File Type</span>
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <FileText className="w-3 h-3 text-white" />
              </div>
            </div>

            {csvError && (
              <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 flex-1">{csvError}</p>
                <button onClick={() => setCsvError(null)}><X className="w-4 h-4 text-red-600 hover:text-red-800" /></button>
              </div>
            )}

            {csvResult && (
              <div className="mx-4 mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-900">{csvResult.message}</p>
                  <p className="text-xs text-green-700 mt-0.5">{csvResult.data?.rowCount} rows processed · Redirecting to Master Data...</p>
                </div>
                <button onClick={() => setCsvResult(null)}><X className="w-4 h-4 text-green-600 hover:text-green-800" /></button>
              </div>
            )}

            <button
              onClick={handleToggleImport}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">Import Product File</span>
              {importExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {importExpanded && (
              <div className="px-4 py-4 bg-gray-50 flex flex-col gap-4 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">CSV File</label>
                  <div className="flex gap-2">
                    <label
                      htmlFor="csv-upload"
                      className={`px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 text-xs whitespace-nowrap transition-colors ${csvLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      Choose File
                    </label>
                    <input type="file" id="csv-upload" accept=".csv" onChange={handleCsvFileChange} ref={csvFileRef} className="hidden" disabled={csvLoading} />
                    <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-xs text-gray-500 truncate flex items-center gap-1">
                      {csvFile ? <><FileText className="w-3 h-3 text-blue-600 flex-shrink-0" />{csvFile.name}</> : 'No file chosen'}
                    </div>
                  </div>
                  {csvFile && <p className="text-xs text-blue-600 mt-1 pl-1">{(csvFile.size / 1024).toFixed(2)} KB</p>}
                </div>

                <div className="bg-white rounded border border-gray-200 p-3">
                  <p className="text-xs text-gray-600"><span className="font-medium">Supported format:</span> CSV files only</p>
                  <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Maximum file size:</span> 10MB</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCsvUpload}
                    disabled={!csvFile || csvLoading}
                    className={`flex-1 py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${csvFile && !csvLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`}
                  >
                    {csvLoading ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload</>}
                  </button>
                  <button onClick={handleCsvCancel} disabled={csvLoading} className="flex-1 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════
            CARD 2 — Import Location Master
            Visible to: Manufacturer only
        ══════════════════════════════════════ */}
        {isManufacturer && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">File Type</span>
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <FileText className="w-3 h-3 text-white" />
              </div>
            </div>

            {locationError && (
              <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 flex-1">{locationError}</p>
                <button onClick={() => setLocationError(null)}><X className="w-4 h-4 text-red-600 hover:text-red-800" /></button>
              </div>
            )}

            {locationResult && (
              <div className="mx-4 mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-900">Upload successful!</p>
                  <p className="text-xs text-green-700 mt-0.5">{locationResult.accepted.length} file(s) accepted · Redirecting to Dashboard...</p>
                </div>
                <button onClick={() => setLocationResult(null)}><X className="w-4 h-4 text-green-600 hover:text-green-800" /></button>
              </div>
            )}

            {locationUploadError && (
              <div className="mx-4 mt-3 bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Upload Results</h3>
                  <button onClick={() => setLocationUploadError(null)}><X className="w-4 h-4 text-gray-400" /></button>
                </div>
                {locationUploadError.accepted?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Accepted ({locationUploadError.accepted.length})</p>
                    {locationUploadError.accepted.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-green-50 border border-green-100 rounded px-3 py-1.5 mb-1">
                        <span className="text-xs text-green-800 font-medium truncate">{f.filename}</span>
                        <span className="text-xs text-green-600 ml-2 flex-shrink-0">{f.rowCount} rows</span>
                      </div>
                    ))}
                  </div>
                )}
                {locationUploadError.rejected?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-700 mb-1 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Failed ({locationUploadError.rejected.length})</p>
                    {locationUploadError.rejected.map((f, i) => (
                      <div key={i} className="bg-red-50 border border-red-100 rounded px-3 py-1.5 mb-1">
                        <p className="text-xs text-red-800 font-medium">{f.filename}</p>
                        <p className="text-xs text-red-600 mt-0.5">{f.message || f.error}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleToggleLocation}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">Import Location Master</span>
              {locationExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {locationExpanded && (
              <div className="px-4 py-4 bg-gray-50 flex flex-col gap-4 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">CSV File(s)</label>
                  <div className="flex gap-2">
                    <label
                      htmlFor="location-csv-upload"
                      className={`px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 text-xs whitespace-nowrap transition-colors ${locationLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      Choose Files
                    </label>
                    <input type="file" id="location-csv-upload" accept=".csv" multiple onChange={handleLocationFileChange} ref={locationFileRef} className="hidden" disabled={locationLoading} />
                    <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-xs text-gray-500 truncate flex items-center gap-1">
                      {locationFiles.length > 0
                        ? <><FileText className="w-3 h-3 text-blue-600 flex-shrink-0" />{locationFiles.length === 1 ? locationFiles[0].name : `${locationFiles.length} files selected`}</>
                        : 'No files chosen'
                      }
                    </div>
                  </div>
                </div>

                {locationFiles.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {locationFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-blue-800 font-medium truncate">{file.name}</p>
                            <p className="text-xs text-blue-500">{(file.size / 1024).toFixed(2)} KB</p>
                          </div>
                        </div>
                        {!locationLoading && (
                          <button onClick={() => removeLocationFile(index)} className="text-blue-400 hover:text-blue-700 ml-2 flex-shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-white rounded border border-gray-200 p-3">
                  <p className="text-xs text-gray-600"><span className="font-medium">Supported format:</span> CSV files only</p>
                  <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Maximum file size:</span> 10MB per file</p>
                  <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Multiple files:</span> Supported</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleLocationUpload}
                    disabled={!locationFiles.length || locationLoading}
                    className={`flex-1 py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${locationFiles.length && !locationLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`}
                  >
                    {locationLoading ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload</>}
                  </button>
                  <button onClick={handleLocationCancel} disabled={locationLoading} className="flex-1 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════
            CARD 3 — Category Instruction (PDF)
            Visible to: Manufacturer only
        ══════════════════════════════════════ */}
        {isManufacturer && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">File Type</span>
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <Tag className="w-3 h-3 text-white" />
              </div>
            </div>

            {catError && (
              <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 flex-1">{catError}</p>
                <button onClick={() => setCatError(null)}><X className="w-4 h-4 text-red-600 hover:text-red-800" /></button>
              </div>
            )}

            {catUploadResult && (
              <div className="mx-4 mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-900">{catUploadResult.message}</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Uploaded for <span className="font-medium">{catSelectedGroup}</span> · <span className="font-medium">{categories.find(c => c.category_key === catSelectedCategory)?.display_name || catSelectedCategory}</span> · Redirecting...
                  </p>
                </div>
                <button onClick={() => setCatUploadResult(null)}><X className="w-4 h-4 text-green-600 hover:text-green-800" /></button>
              </div>
            )}

            <button
              onClick={handleToggleCategory}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <span className="text-sm text-gray-700">Category Instruction</span>
              {categoryExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {categoryExpanded && (
              <div className="px-4 py-4 bg-gray-50 flex flex-col gap-4">

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Select Product Group</label>
                  {groupsLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-xs text-gray-500">Loading groups...</span>
                    </div>
                  ) : productGroups.length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">No product groups found.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {productGroups.map((group) => (
                        <label
                          key={group.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                            catSelectedGroup === group.productName
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="catProductGroup"
                            value={group.productName}
                            checked={catSelectedGroup === group.productName}
                            onChange={(e) => {
                              setCatSelectedGroup(e.target.value);
                              setCatExistingData(null);
                            }}
                            className="accent-blue-600"
                          />
                          <span className="text-sm text-gray-700">{group.productName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Select Category</label>
                  {categoriesLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-xs text-gray-500">Loading categories...</span>
                    </div>
                  ) : (
                    <select
                      value={catSelectedCategory}
                      onChange={(e) => {
                        setCatSelectedCategory(e.target.value);
                        setCatExistingData(null);
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">— Select a category —</option>
                      {categories.map((cat) => (
                        <option key={cat.category_key} value={cat.category_key}>
                          {cat.display_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {catSelectedGroup && catSelectedCategory && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-600">Current Data</span>
                      <span className="text-xs text-gray-400">
                        — {categories.find(c => c.category_key === catSelectedCategory)?.display_name || catSelectedCategory}
                      </span>
                    </div>
                    {catExistingLoading ? (
                      <div className="flex items-center gap-2 py-2">
                        <Loader className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-xs text-gray-500">Loading...</span>
                      </div>
                    ) : catExistingData ? (
                      <div className="bg-white rounded border border-gray-200 p-3 max-h-28 overflow-y-auto">
                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {catExistingData.extracted_text || catExistingData.instructions_text || '—'}
                        </p>
                        <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                          Last updated:{' '}
                          {catExistingData.updated_at ? new Date(catExistingData.updated_at).toLocaleDateString() : '—'}
                          {catExistingData.pdf_filename && (
                            <span className="ml-2">· {catExistingData.pdf_filename}</span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white rounded border border-dashed border-gray-300 p-3 text-center">
                        <p className="text-xs text-gray-400">No data uploaded yet for this group and category.</p>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">PDF File</label>
                  <div className="flex gap-2">
                    <label
                      htmlFor="cat-pdf-upload"
                      className={`px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 text-xs whitespace-nowrap transition-colors ${catLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      Choose File
                    </label>
                    <input
                      type="file"
                      id="cat-pdf-upload"
                      accept=".pdf,application/pdf"
                      onChange={handleCatFileChange}
                      ref={catFileInputRef}
                      className="hidden"
                      disabled={catLoading}
                    />
                    <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-xs text-gray-500 truncate flex items-center gap-1">
                      {catSelectedFile
                        ? <><FileText className="w-3 h-3 text-blue-600 flex-shrink-0" />{catSelectedFile.name}</>
                        : 'No file chosen'
                      }
                    </div>
                  </div>
                  {catSelectedFile && (
                    <p className="text-xs text-blue-600 mt-1 pl-1">{(catSelectedFile.size / 1024).toFixed(2)} KB</p>
                  )}
                </div>

                <div className="bg-white rounded border border-gray-200 p-3">
                  <p className="text-xs text-gray-600"><span className="font-medium">Supported format:</span> PDF files only</p>
                  <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Maximum file size:</span> 20MB</p>
                  <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Note:</span> Uploading a new PDF will replace existing data for the selected group and category.</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCatUpload}
                    disabled={!catSelectedFile || !catSelectedGroup || !catSelectedCategory || catLoading}
                    className={`flex-1 py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      catSelectedFile && catSelectedGroup && catSelectedCategory && !catLoading
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-400 cursor-not-allowed'
                    }`}
                  >
                    {catLoading ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload</>}
                  </button>
                  <button
                    onClick={handleCatCancel}
                    disabled={catLoading}
                    className="flex-1 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>

              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════
            CARD 4 — Disassembly Instruction
            Manufacturer : upload PDF
            Repair/Refurb: view only
        ══════════════════════════════════════ */}
        {(isManufacturer || isRepairRefurb) && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">File Type</span>
              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <FileText className="w-3 h-3 text-white" />
              </div>
            </div>

            {isManufacturer && disassemblyError && (
              <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 flex-1">{disassemblyError}</p>
                <button onClick={() => setDisassemblyError(null)}><X className="w-4 h-4 text-red-600 hover:text-red-800" /></button>
              </div>
            )}

            {isManufacturer && uploadResult && (
              <div className="mx-4 mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-900">{uploadResult.message}</p>
                  <p className="text-xs text-green-700 mt-0.5">Instructions uploaded for <span className="font-medium">{selectedGroup}</span> · Redirecting to Dashboard...</p>
                </div>
                <button onClick={() => setUploadResult(null)}><X className="w-4 h-4 text-green-600 hover:text-green-800" /></button>
              </div>
            )}

            <button
              onClick={handleToggleDisassembly}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <span className="text-sm text-gray-700">Disassembly Instruction</span>
              {disassemblyExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {disassemblyExpanded && (
              <div className="px-4 py-4 bg-gray-50 flex flex-col gap-4">

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Select Product Group</label>
                  {groupsLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-xs text-gray-500">Loading groups...</span>
                    </div>
                  ) : productGroups.length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">No product groups found.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {productGroups.map((group) => (
                        <label
                          key={group.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedGroup === group.productName ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="productGroup"
                            value={group.productName}
                            checked={selectedGroup === group.productName}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            className="accent-blue-600"
                          />
                          <span className="text-sm text-gray-700">{group.productName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {selectedGroup && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-600">Current Instructions</span>
                      <span className="text-xs text-gray-400">— {selectedGroup}</span>
                    </div>
                    {instructionsLoading ? (
                      <div className="flex items-center gap-2 py-2">
                        <Loader className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-xs text-gray-500">Loading...</span>
                      </div>
                    ) : existingInstructions ? (
                      <div className="bg-white rounded border border-gray-200 p-3 max-h-28 overflow-y-auto">
                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {existingInstructions.instructions_text || existingInstructions.instructions}
                        </p>
                        <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                          Last updated:{' '}
                          {existingInstructions.updated_at ? new Date(existingInstructions.updated_at).toLocaleDateString() : '—'}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white rounded border border-dashed border-gray-300 p-3 text-center">
                        <p className="text-xs text-gray-400">No instructions uploaded yet for this group.</p>
                      </div>
                    )}
                  </div>
                )}

                {isManufacturer && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">PDF File</label>
                      <div className="flex gap-2">
                        <label
                          htmlFor="pdf-upload"
                          className={`px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 text-xs whitespace-nowrap transition-colors ${disassemblyLoading ? 'opacity-50 pointer-events-none' : ''}`}
                        >
                          Choose File
                        </label>
                        <input type="file" id="pdf-upload" accept=".pdf,application/pdf" onChange={handleFileChange} ref={fileInputRef} className="hidden" disabled={disassemblyLoading} />
                        <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-xs text-gray-500 truncate flex items-center gap-1">
                          {selectedFile ? <><FileText className="w-3 h-3 text-blue-600 flex-shrink-0" />{selectedFile.name}</> : 'No file chosen'}
                        </div>
                      </div>
                      {selectedFile && <p className="text-xs text-blue-600 mt-1 pl-1">{(selectedFile.size / 1024).toFixed(2)} KB</p>}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleUpload}
                        disabled={!selectedFile || !selectedGroup || disassemblyLoading}
                        className={`flex-1 py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${selectedFile && selectedGroup && !disassemblyLoading ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`}
                      >
                        {disassemblyLoading ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload</>}
                      </button>
                      <button onClick={handleCancel} disabled={disassemblyLoading} className="flex-1 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        Cancel
                      </button>
                    </div>
                  </>
                )}

              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default UploadData;